
import React, { useState, useEffect, useRef } from 'react';
import { Service, LandingSection, PortfolioItem, AppSettings, KaosSettings } from './types';
import { INITIAL_SERVICES, INITIAL_SECTIONS, APP_SETTINGS, INITIAL_PORTFOLIOS, INITIAL_KAOS_SETTINGS } from './constants';
import { Icon } from './components/Icon';
import { AdminPanel } from './components/AdminPanel';
import { LoginModal } from './components/LoginModal';
import PortfolioWebsite from './PortfolioWebsite';
import PortfolioApp from './PortfolioApp';
import PortfolioMultimedia from './PortfolioMultimedia';
import ProductKaos from './ProductKaos';
import ServicesPage from './ServicesPage';
import AboutUs from './components/AboutUs';
import { getAppData, auth, onAuthStateChanged, logVisitor } from './firebase';

type ViewState = 'landing' | 'portfolio-website' | 'portfolio-aplikasi' | 'portfolio-multimedia' | 'product-kaos' | 'services-page';

const App: React.FC = () => {
  const [services, setServices] = useState<Service[]>(INITIAL_SERVICES);
  const [sections, setSections] = useState<LandingSection[]>(INITIAL_SECTIONS);
  const [portfolios, setPortfolios] = useState<PortfolioItem[]>(INITIAL_PORTFOLIOS);
  const [appSettings, setAppSettings] = useState<AppSettings>(APP_SETTINGS);
  const [kaosSettings, setKaosSettings] = useState<KaosSettings>(INITIAL_KAOS_SETTINGS);
  
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentView, setCurrentView] = useState<ViewState>('landing');
  const [isLoading, setIsLoading] = useState(true);

  // Track page view changes for analytics
  useEffect(() => {
    logVisitor(currentView);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      if (!user) setIsAdminOpen(false);
    });

    const loadFirebaseData = async () => {
      const data = await getAppData();
      if (data) {
        if (data.services) setServices(data.services);
        if (data.sections) setSections(data.sections);
        if (data.portfolios) setPortfolios(data.portfolios);
        if (data.appSettings) setAppSettings(data.appSettings);
        if (data.kaosSettings) setKaosSettings(data.kaosSettings);
      }
      setIsLoading(false);
    };

    loadFirebaseData();
    
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      unsubscribe();
    };
  }, []);

  const handleAdminAccess = () => {
    isAuthenticated ? setIsAdminOpen(true) : setIsLoginOpen(true);
  };

  // Robust navigation logic using ID, Category, or Keywords
  const navigateToProduct = (service: Service) => {
    const id = service.id.toLowerCase();
    const title = service.title.toLowerCase();
    const cat = service.category;

    if (id === 'kaos-custom' || cat === 'Apparel' || title.includes('kaos')) {
      setCurrentView('product-kaos');
    } else if (id === 'web-dev' || title.includes('website')) {
      setCurrentView('portfolio-website');
    } else if (id === 'app-dev' || title.includes('aplikasi') || title.includes('app')) {
      setCurrentView('portfolio-aplikasi');
    } else if (id === 'multimedia-interaktif' || cat === 'Media' || title.includes('multimedia')) {
      setCurrentView('portfolio-multimedia');
    } else {
      setCurrentView('services-page');
    }
  };

  const renderSections = () => {
    return sections.map((section) => {
      if (!section.isVisible) return null;

      switch (section.type) {
        case 'hero':
          return (
            <section key={section.id} id="hero" className="relative min-h-[90vh] flex items-center pt-20 overflow-hidden text-white">
              <div className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-600/20 rounded-full blur-[120px]"></div>
              <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-600/20 rounded-full blur-[120px]"></div>
              <div className="container mx-auto px-6 relative z-10 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-morphism mb-6 border border-white/10">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  <span className="text-xs font-bold text-slate-300 tracking-wider uppercase">Teknologi Masa Depan Sudah Di Sini</span>
                </div>
                <h1 className="text-5xl md:text-8xl font-extrabold mb-8 leading-[1.1]">
                   {section.title}
                </h1>
                <p className="text-xl text-slate-400 mb-12 max-w-3xl mx-auto leading-relaxed">
                  {section.subtitle}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                  <a href={`https://wa.me/${appSettings.contactWhatsapp}`} target="_blank" rel="noopener" className="w-full sm:w-auto px-12 py-5 bg-purple-600 hover:bg-purple-700 rounded-full font-extrabold text-xl transition-all flex items-center justify-center gap-3 shadow-2xl shadow-purple-900/40">
                    Konsultasi Gratis <Icon name="MessageSquare" size={24} />
                  </a>
                  <button onClick={() => setCurrentView('services-page')} className="w-full sm:w-auto px-12 py-5 glass-morphism border border-white/20 rounded-full font-extrabold text-xl hover:bg-white/10 transition-all">
                    Lihat Layanan
                  </button>
                </div>
              </div>
            </section>
          );

        case 'about':
          return <AboutUs key={section.id} />;

        case 'features':
          return (
            <section key={section.id} id="services" className="py-24 bg-[#030712] text-white">
              <div className="container mx-auto px-6">
                <div className="text-center mb-20">
                  <h2 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight">{section.title}</h2>
                  <p className="text-slate-400 max-w-2xl mx-auto text-lg">{section.subtitle}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                  {services.map((service) => (
                    <div 
                        key={service.id} 
                        onClick={() => navigateToProduct(service)}
                        className="group relative p-10 glass-morphism rounded-[3rem] border border-white/10 hover:border-purple-500/50 transition-all duration-500 flex flex-col h-full cursor-pointer hover:-translate-y-2"
                    >
                      <div className="flex justify-between items-start mb-8">
                        <div className="p-5 bg-purple-600/10 rounded-2xl group-hover:bg-purple-600 group-hover:text-white transition-all duration-500">
                          <Icon name={service.icon} className="text-purple-400 group-hover:text-white" size={36} />
                        </div>
                        <div className="px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full">
                            <span className="text-[10px] font-black text-purple-400 uppercase tracking-[0.2em]">{service.priceRange || 'Contact Us'}</span>
                        </div>
                      </div>
                      <h3 className="text-3xl font-bold mb-4 group-hover:gradient-text transition-all">{service.title}</h3>
                      <p className="text-slate-400 mb-8 text-sm flex-grow leading-relaxed">{service.description}</p>
                      
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToProduct(service);
                        }}
                        className="w-full mb-8 py-3 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-2xl text-[11px] font-black hover:bg-blue-600 hover:text-white transition-all uppercase tracking-widest shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2"
                      >
                         Lihat Detail {service.category === 'Apparel' ? 'Produk' : 'Portofolio'} <Icon name="ArrowRight" size={14} />
                      </button>

                      <ul className="space-y-3">
                        {service.features.map((f, i) => (
                          <li key={i} className="flex items-center gap-3 text-[11px] text-slate-300 font-medium">
                            <Icon name="CheckCircle2" className="text-green-500" size={16} /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );

        case 'aida':
          return (
            <section key={section.id} className="py-24 relative text-white overflow-hidden">
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-600/10 rounded-full blur-[150px] -z-10"></div>
              <div className="container mx-auto px-6">
                <div className="glass-morphism rounded-[4rem] p-12 md:p-20 border border-white/10 bg-gradient-to-br from-white/[0.02] to-transparent shadow-2xl">
                  <div className="text-center max-w-4xl mx-auto">
                    <h2 className="text-4xl md:text-6xl font-black mb-10 leading-[1.1]">Kenapa Memilih <br/><span className="gradient-text">BisaBikin.id?</span></h2>
                    <p className="text-slate-400 text-xl mb-16 leading-relaxed">Kami bukan sekadar agensi digital. Kami adalah partner transformasi Anda yang menggabungkan kecepatan AI dengan standar kualitas premium untuk hasil yang 3x lebih cepat dan jauh lebih hemat biaya.</p>
                    
                    <div className="overflow-hidden rounded-[2.5rem] border border-white/10 mb-16 bg-black/40 shadow-2xl backdrop-blur-xl">
                       <table className="w-full text-left text-sm md:text-base">
                          <thead>
                             <tr className="bg-white/5 border-b border-white/10">
                                <th className="p-6 md:p-8 font-black text-slate-500 uppercase tracking-widest text-xs md:text-sm">Keunggulan Utama</th>
                                <th className="p-6 md:p-8 font-bold text-slate-400 text-center text-xs md:text-sm">Kompetitor Lain</th>
                                <th className="p-6 md:p-8 font-black text-purple-400 text-center bg-purple-500/10 text-xs md:text-sm">BisaBikin.id</th>
                             </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5 text-slate-300">
                             <tr className="hover:bg-white/5 transition-colors">
                                <td className="p-6 md:p-8 border-r border-white/5 font-bold">Teknologi Website</td>
                                <td className="p-6 md:p-8 text-center text-slate-500 italic">Konvensional (Lambat)</td>
                                <td className="p-6 md:p-8 text-center text-white font-extrabold bg-purple-500/10">Instant SPA (Sangat Cepat)</td>
                             </tr>
                             <tr className="hover:bg-white/5 transition-colors">
                                <td className="p-6 md:p-8 border-r border-white/5 font-bold">Waktu Pengerjaan</td>
                                <td className="p-6 md:p-8 text-center text-slate-500 italic">2-4 Minggu (Lama)</td>
                                <td className="p-6 md:p-8 text-center text-white font-extrabold bg-purple-500/10">3-7 Hari Selesai!</td>
                             </tr>
                             <tr className="hover:bg-white/5 transition-colors">
                                <td className="p-6 md:p-8 border-r border-white/5 font-bold">Efisiensi Harga</td>
                                <td className="p-6 md:p-8 text-center text-slate-500 italic">Mahal & Charge Tambahan</td>
                                <td className="p-6 md:p-8 text-center text-green-400 font-extrabold bg-purple-500/10">Sangat Murah & Transparan</td>
                             </tr>
                             <tr className="hover:bg-white/5 transition-colors">
                                <td className="p-6 md:p-8 border-r border-white/5 font-bold">Dukungan AI</td>
                                <td className="p-4 md:p-8 text-center text-slate-500 italic">Belum Ada / Terbatas</td>
                                <td className="p-4 md:p-8 text-center text-white font-extrabold bg-purple-500/10">Full AI Integrated Services</td>
                             </tr>
                          </tbody>
                       </table>
                    </div>

                    <div className="flex flex-col md:flex-row gap-6 justify-center">
                        <button onClick={() => setCurrentView('services-page')} className="px-12 py-5 bg-white text-black rounded-3xl font-black text-xl hover:scale-105 transition-all shadow-2xl shadow-white/10">Lihat Semua Keunggulan</button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );

        case 'cta':
          return (
            <section key={section.id} className="py-24 bg-gradient-to-br from-blue-600 via-purple-700 to-pink-600 text-white text-center">
              <div className="container mx-auto px-6">
                <h2 className="text-4xl md:text-6xl font-black mb-8 leading-tight">{section.title}</h2>
                <p className="text-xl md:text-2xl opacity-90 mb-12 max-w-3xl mx-auto leading-relaxed">{section.subtitle}</p>
                <a href={`https://wa.me/${appSettings.contactWhatsapp}`} target="_blank" rel="noopener" className="inline-flex items-center gap-4 px-12 py-5 bg-white text-black rounded-3xl font-black text-xl hover:bg-slate-100 hover:-translate-y-1 transition-all shadow-2xl shadow-black/20">Mulai Kolaborasi <Icon name="ArrowRight" size={24} /></a>
              </div>
            </section>
          );

        default:
          return null;
      }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center text-white">
        <div className="flex flex-col items-center gap-6">
            <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin shadow-lg shadow-purple-900/50"></div>
            <p className="text-slate-500 font-bold uppercase tracking-[0.3em] animate-pulse">Menyiapkan Pengalaman Digital...</p>
        </div>
      </div>
    );
  }

  // Routing Logic
  if (currentView === 'portfolio-website') return <PortfolioWebsite items={portfolios} onBack={() => setCurrentView('landing')} contactNumber={appSettings.contactWhatsapp} />;
  if (currentView === 'portfolio-aplikasi') return <PortfolioApp items={portfolios} onBack={() => setCurrentView('landing')} contactNumber={appSettings.contactWhatsapp} />;
  if (currentView === 'portfolio-multimedia') return <PortfolioMultimedia items={portfolios} onBack={() => setCurrentView('landing')} />;
  if (currentView === 'product-kaos') return <ProductKaos items={portfolios} settings={kaosSettings} onBack={() => setCurrentView('landing')} contactNumber={appSettings.contactWhatsapp} />;
  if (currentView === 'services-page') return <ServicesPage services={services} onBack={() => setCurrentView('landing')} onNavigateToPortfolio={(t) => setCurrentView(t === 'kaos' ? 'product-kaos' : `portfolio-${t === 'web' ? 'website' : t === 'app' ? 'aplikasi' : t}` as any)} />;

  return (
    <div className="min-h-screen selection:bg-purple-500/30 selection:text-white">
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-[#030712]/80 backdrop-blur-2xl border-b border-white/5 py-4' : 'bg-transparent py-8'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center text-white">
          <button onClick={() => setCurrentView('landing')} className="flex items-center gap-3 font-black text-2xl uppercase tracking-tighter group">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-all">B</div> 
            <span className="group-hover:text-purple-400 transition-colors">{appSettings.brandName}</span>
          </button>
          <div className="hidden md:flex items-center gap-10">
            <button onClick={() => setCurrentView('services-page')} className="text-sm font-bold text-slate-300 hover:text-white transition-colors uppercase tracking-widest">Layanan</button>
            <button onClick={() => setCurrentView('product-kaos')} className="text-sm font-bold text-slate-300 hover:text-white transition-colors uppercase tracking-widest">Kaos Custom</button>
            <a href={`https://wa.me/${appSettings.contactWhatsapp}`} target="_blank" rel="noopener" className="px-8 py-3 bg-white text-black rounded-2xl text-sm font-black shadow-xl shadow-white/5 hover:scale-105 transition-all uppercase tracking-widest">Konsultasi</a>
          </div>
        </div>
      </nav>
      <main>{renderSections()}</main>
      <footer className="py-24 bg-[#030712] border-t border-white/5 text-center">
         <div className="flex justify-center gap-10 mb-12">
            <button onClick={() => setCurrentView('services-page')} className="text-slate-500 hover:text-white transition-all text-xs font-bold uppercase tracking-widest">Layanan</button>
            <button onClick={() => setCurrentView('product-kaos')} className="text-slate-500 hover:text-white transition-all text-xs font-bold uppercase tracking-widest">Custom Kaos</button>
            <button onClick={() => setCurrentView('portfolio-website')} className="text-slate-500 hover:text-white transition-all text-xs font-bold uppercase tracking-widest">Portofolio</button>
         </div>
         <p className="text-slate-700 text-[10px] uppercase tracking-[0.5em] font-black mb-8">&copy; 2025 {appSettings.brandName}. Engineered for Future.</p>
         <button onClick={handleAdminAccess} className="w-4 h-4 bg-slate-900 rounded-full mx-auto block hover:bg-purple-600 transition-all border border-white/5"></button>
      </footer>
      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} onSuccess={() => setIsAdminOpen(true)} />
      <AdminPanel 
        isOpen={isAdminOpen} onClose={() => setIsAdminOpen(false)} 
        services={services} setServices={setServices} 
        sections={sections} setSections={setSections} 
        portfolios={portfolios} setPortfolios={setPortfolios} 
        appSettings={appSettings} setAppSettings={setAppSettings} 
        kaosSettings={kaosSettings} setKaosSettings={setKaosSettings}
      />
    </div>
  );
};

export default App;
